/**
 * The drawer's Activity tab body (Evo/PSC). The object-level history timeline
 * spanning EVERY visible code on the record (via ``useExceptionObjectHistory``,
 * no canonical collapse), rendered as the full tab body - no accordion chrome.
 * A muted "last confirmed by sync" sub-line sits beneath it.
 */
import React from 'react'
import { Box, Typography } from '@mui/material'
import StatusHistoryTimeline from '../StatusHistoryTimeline'
import type { DisplayTimelineEvent } from '../StatusHistoryTimeline'
import { formatRelativeTimestamp } from '../../../../utils/dateFormatters'
import { poolwerxColors } from '../../../../theme/colors'

interface ActivityPanelProps {
  events: DisplayTimelineEvent[]
  loading: boolean
  error: string | null
  lastConfirmedAt: string | null
  /** Focused code - bolded "you are here" marker in the timeline. */
  currentCode?: string
}

const ActivityPanel: React.FC<ActivityPanelProps> = ({
  events,
  loading,
  error,
  lastConfirmedAt,
  currentCode,
}) => {
  const confirmed = lastConfirmedAt
    ? `Last confirmed by sync - ${formatRelativeTimestamp(lastConfirmedAt).label}`
    : undefined

  return (
    <Box sx={{ padding: '18px 22px 0' }}>
      <StatusHistoryTimeline
        events={events}
        loading={loading}
        error={error}
        currentCode={currentCode}
      />
      {!loading && !error && events.length === 0 && (
        <Typography
          variant="body2"
          sx={{ color: 'text.secondary', fontStyle: 'italic', py: 1 }}
        >
          No activity yet.
        </Typography>
      )}
      {confirmed && (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            mt: '8px',
            pt: '12px',
            pb: '12px',
            borderTop: `0.5px solid ${poolwerxColors.border.tertiary}`,
            color: poolwerxColors.text.tertiary,
            fontSize: '11.5px',
          }}
        >
          {confirmed}
        </Typography>
      )}
    </Box>
  )
}

export default ActivityPanel
